import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Checks if a value is a valid Date object
 * @param date - Value to check
 * @returns Boolean indicating if value is a valid Date object
 */
export const isValidDate = (date: any): date is Date => {
  return date instanceof Date && !isNaN(date.getTime());
};

/**
 * Checks if a string is a valid ISO datetime string
 * @param dateString - String to check
 * @returns Boolean indicating if string is valid ISO format
 */
export const isISOString = (dateString: string): boolean => {
  if (!dateString || typeof dateString !== "string") return false;

  // Check for ISO 8601 patterns (with or without time)
  const isoRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?)?$/;

  if (!isoRegex.test(dateString)) return false;

  // Validate with dayjs
  return dayjs(dateString).isValid();
};

/**
 * Checks if a date string has time component
 * @param dateString - Date string to check
 * @returns Boolean indicating if time is included
 */
export const hasTimeComponent = (dateString: string): boolean => {
  return typeof dateString === "string" && dateString.includes("T");
};

/**
 * Converts datetime string to Date object
 * @param datetimeString - ISO datetime string
 * @returns Date object or null if invalid
 */
export const toDate = (datetimeString: string): Date | null => {
  if (!isISOString(datetimeString)) return null;

  const date = dayjs(datetimeString);
  return date.isValid() ? date.toDate() : null;
};

/**
 * Converts Date object to datetime string
 * @param date - Date object
 * @returns ISO datetime string
 */
export const toDateTimeString = (date: Date): string => {
  return dayjs(date).format("YYYY-MM-DDTHH:mm:ss");
};

/**
 * Gets a date value with proper fallbacks for access cards
 * @param accessCardsDate - Date from accessCards (Date object or ISO string)
 * @param mainContentDate - Date from mainContent as fallback (Date object or ISO string)
 * @returns Datetime string for backend
 */
export const getAccessCardDate = (
  accessCardsDate?: Date | string,
  mainContentDate?: Date | string
): string => {
  // Try accessCards date first
  if (accessCardsDate) {
    if (isValidDate(accessCardsDate)) {
      return dayjs(accessCardsDate).format("YYYY-MM-DDTHH:mm:ss");
    }
    if (typeof accessCardsDate === "string" && isISOString(accessCardsDate)) {
      return accessCardsDate;
    }
  }

  // Try mainContent date as fallback
  if (mainContentDate) {
    if (isValidDate(mainContentDate)) {
      return dayjs(mainContentDate).format("YYYY-MM-DDTHH:mm:ss");
    }
    if (typeof mainContentDate === "string" && isISOString(mainContentDate)) {
      return mainContentDate;
    }
  }

  // Default to today with 10:00 AM
  return dayjs().hour(10).minute(0).second(0).format("YYYY-MM-DDTHH:mm:ss");
};

/**
 * Formats a datetime string for display in the DatePicker input
 * @param datetimeString - ISO datetime string
 * @param includeTime - Whether to show time
 * @returns Formatted display string
 */
export const formatForDisplay = (
  datetimeString: string,
  includeTime = false
): string => {
  if (!datetimeString || !isISOString(datetimeString)) {
    return "";
  }

  const date = dayjs(datetimeString);

  if (includeTime && hasTimeComponent(datetimeString)) {
    // Format: "27/10/2025, 10:00 AM"
    const dateStr = date.format("DD/MM/YYYY");
    const timeStr = date.format("h:mm A");
    return `${dateStr}, ${timeStr}`;
  }

  // Format: "Wed, 27 Oct 2025"
  return date.format("ddd, DD MMM YYYY");
};

/**
 * Formats time for display in the preview
 * @param datetimeString - ISO datetime string
 * @param showPrompt - Whether to append "Prompt" to the time
 * @returns Formatted time string
 */
export const formatTimeForPreview = (
  datetimeString?: string,
  showPrompt = true
): string => {
  if (!datetimeString || !isISOString(datetimeString)) {
    return showPrompt ? "10:00 AM Prompt" : "10:00 AM";
  }

  const date = dayjs(datetimeString);
  const timeStr = date.format("h:mm A");

  return showPrompt ? `${timeStr} Prompt` : timeStr;
};

/**
 * Formats a date for preview
 * @param date - Date object or ISO string to format
 * @returns Formatted string like "Wed, 27 Oct 2025"
 */
export const formatDateForPreview = (date: Date | string): string => {
  const dayjsDate = dayjs(date);
  return dayjsDate.format("ddd DD MMM YYYY");
};

/**
 * Formats a date to DD/MM/YY, h:mmA format
 * @param date - Date object or ISO string to format
 * @returns Formatted string like "27/12/25, 10:52AM"
 */
export const formatDateAndTime = (date: Date | string): string => {
  const dayjsDate = dayjs(date);
  const formattedDate = dayjsDate.format("DD/MM/YY");
  const formattedTime = dayjsDate.format("h:mmA");
  return `${formattedDate}, ${formattedTime}`;
};

/**
 * Creates datetime string from date and time components
 * @param dateOnly - Date part (YYYY-MM-DD)
 * @param hour12 - Hour in 12-hour format (1-12)
 * @param minute - Minute (0-59)
 * @param period - AM or PM
 * @returns ISO datetime string for backend
 */
export const createDateTimeString = (
  dateOnly: string,
  hour12: number,
  minute: number,
  period: "AM" | "PM"
): string => {
  let hour24 = hour12;

  if (period === "PM" && hour12 !== 12) {
    hour24 += 12;
  } else if (period === "AM" && hour12 === 12) {
    hour24 = 0;
  }

  return `${dateOnly}T${String(hour24).padStart(2, "0")}:${String(
    minute
  ).padStart(2, "0")}:00`;
};

/**
 * Extracts time components from a datetime string
 * @param datetimeString - ISO datetime string
 * @returns Object with hour12, minute, and period
 */
export const extractTimeComponents = (datetimeString: string) => {
  if (!datetimeString || !isISOString(datetimeString)) {
    return { hour12: 10, minute: 0, period: "AM" as const };
  }

  const date = dayjs(datetimeString);
  const hour24 = date.hour();
  const minute = date.minute();

  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
  const period = hour24 >= 12 ? ("PM" as const) : ("AM" as const);

  return { hour12, minute, period };
};

/**
 * Formats a date to "MMM DD YYYY, HH:mm" format
 * @param date - Date object or ISO string to format
 * @returns Formatted string like "Jun 25 2025, 05:25"
 */
export const formatToCustomDisplay = (date: Date | string): string => {
  const dayjsDate = dayjs(date);
  return dayjsDate.format("MMM DD YYYY, HH:mm");
};
